import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { InfoCard } from "@/components/result/InfoCard";
import { ExternalLinkConfirm } from "@/components/result/ExternalLinkConfirm";
import { buildPlantLinks } from "@/lib/plantLinks";

/**
 * Native-alternatives suggestion card for the Result page.
 *
 * Three states:
 *   - alts === null   → hasn't been fetched yet; render the CTA button
 *   - alts.length === 0 → we fetched but the model returned nothing
 *   - alts.length > 0 → render the list, each row with external "Learn more"
 *                       links routed through the ExternalLinkConfirm dialog.
 *
 * The parent owns loading state + the Gemini call — this component is
 * purely presentational + a single onLoad callback.
 */
export const AlternativesCard = ({
  alts,
  altsLoading,
  scanState,
  plantType,
  onLoad,
}) => {
  const { t } = useTranslation();
  return (
    <InfoCard
      title={t("result.altsTitle", { state: scanState })}
      icon={<Sparkles className="w-4 h-4" strokeWidth={1.5} />}
      testId="card-alternatives"
    >
      {alts === null ? (
        <>
          <p className="text-leaf-mist mb-3">
            {plantType === "weed"
              ? t("result.altsWeed", { state: scanState })
              : t("result.altsNonNative", { state: scanState })}
          </p>
          <button
            onClick={onLoad}
            disabled={altsLoading}
            data-testid="load-alternatives-button"
            className="rounded-full px-5 py-2.5 bg-leaf text-white font-medium text-sm hover:bg-leaf-deep disabled:opacity-60 transition active:scale-[0.98] flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            {altsLoading ? t("result.finding") : t("result.findAlts")}
          </button>
        </>
      ) : alts.length === 0 ? (
        <p className="text-leaf-mist">{t("result.noAlts")}</p>
      ) : (
        <ul className="space-y-3" data-testid="alternatives-list">
          {alts.map((a, i) => {
            // Reuse `buildPlantLinks` so each alternative gets the SAME
            // curated set of horticultural references the main plant card
            // uses — USDA PLANTS, iNaturalist, state-specific wildflower
            // site, Xerces Society (when keystone-y), etc. Every one is
            // gated behind the confirmation dialog.
            const links = buildPlantLinks({
              identification: {
                common_name: a.common_name,
                scientific_name: a.scientific_name,
              },
              state: scanState,
            });
            return (
              <li
                key={a.scientific_name || a.common_name || `alt-${i}`}
                data-testid={`alternative-item-${i}`}
                className="bg-parchment rounded-2xl p-3"
              >
                <p className="font-display text-xl text-leaf-ink leading-tight">{a.common_name}</p>
                {a.scientific_name && (
                  <p className="italic text-[12px] text-leaf-mist">{a.scientific_name}</p>
                )}
                {a.why && <p className="mt-1.5 text-[14px] text-leaf-ink/90">{a.why}</p>}
                {links.length > 0 && (
                  <div
                    className="mt-2 flex flex-wrap gap-1.5"
                    data-testid={`alternative-item-${i}-links`}
                  >
                    {links.slice(0, 4).map((lnk) => (
                      <ExternalLinkConfirm
                        key={lnk.key}
                        href={lnk.url}
                        label={lnk.label}
                        source={lnk.label}
                        testId={`alt-${i}-link-${lnk.key}`}
                        plantName={a.common_name || a.scientific_name}
                      />
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </InfoCard>
  );
};

export default AlternativesCard;
